import { describeError, useApi } from '@classgo/app-kit';
import type {
  FamilyAccountView,
  FeeConceptCode,
  FeeConceptView,
  GenerateChargesResult,
} from '@classgo/shared';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Modal,
  Select,
  Table,
  Tabs,
  useToast,
} from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { listQuery } from '../../academic/api';
import { billingApi, billingQueries } from '../../billing/api';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/dates';
import { formatMoney, saveBlob, today } from '../../lib/money';
import { BillingCalendarTab } from './calendar';
import { RemindersTab } from './reminders';

const CODES: FeeConceptCode[] = ['matricula', 'pension', 'transporte', 'alimentacion', 'otro'];

/**
 * The secretary's billing screen (ticket #70): concepts and tariffs of the active year,
 * families with their balance, charge generation with a preview, the invoicing export, and
 * since #117 the calendar of the recurring concepts and the school's reminder days.
 */
export const BillingScreen = () => {
  const t = useT();
  const [tab, setTab] = useState('families');
  return (
    <Tabs
      label={t('billing.title')}
      value={tab}
      onChange={setTab}
      items={[
        { id: 'families', label: t('billing.tab.families'), content: <Families /> },
        { id: 'concepts', label: t('billing.tab.concepts'), content: <Concepts /> },
        { id: 'generate', label: t('billing.tab.generate'), content: <Generate /> },
        { id: 'calendar', label: t('billing.tab.calendar'), content: <Calendar /> },
        { id: 'reminders', label: t('billing.tab.reminders'), content: <RemindersTab /> },
        { id: 'export', label: t('billing.tab.export'), content: <Export /> },
      ]}
    />
  );
};

const useActiveYear = () => {
  const api = useApi();
  const years = useQuery(listQuery(api, 'years'));
  return (years.data?.items ?? []).find((year) => year.status === 'active');
};

const Calendar = () => {
  const t = useT();
  const year = useActiveYear();
  if (!year)
    return (
      <Card title={t('billing.tab.calendar')}>
        <p className="text-sm text-gray-500">{t('student.enroll.noYear')}</p>
      </Card>
    );
  return <BillingCalendarTab year={year} />;
};

const Families = () => {
  const t = useT();
  const api = useApi();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [withBalance, setWithBalance] = useState(false);
  const families = useQuery(
    billingQueries.families(api, {
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(withBalance ? { withBalance: true } : {}),
    }),
  );
  return (
    <Card title={t('billing.families.title')} flush>
      <div className="grid gap-3 px-4 pt-4 sm:grid-cols-2">
        <Input
          label={t('students.filter.search')}
          help={t('billing.families.search.help')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="flex items-end pb-2">
          <Checkbox
            label={t('billing.families.withBalance')}
            checked={withBalance}
            onChange={(event) => setWithBalance(event.target.checked)}
          />
        </div>
      </div>
      {families.isError ? (
        <div className="p-6">
          <EmptyState
            title={t('entity.error.title')}
            description={describeError(t, families.error)}
          />
        </div>
      ) : (
        <Table<FamilyAccountView>
          caption={t('billing.families.caption')}
          rows={families.data?.items ?? []}
          rowKey={(row) => row.guardianId}
          loading={families.isPending}
          onRowClick={(row) =>
            void navigate({
              to: '/cobros/familias/$guardianId',
              params: { guardianId: row.guardianId },
            })
          }
          empty={
            <EmptyState
              title={t('billing.families.empty.title')}
              description={t('billing.families.empty.body')}
            />
          }
          columns={[
            {
              key: 'name',
              header: t('billing.families.column.guardian'),
              cell: (row) => (
                <div>
                  <p className="font-medium text-gray-900">
                    {row.familyName}, {row.givenName}
                  </p>
                  <p className="text-xs text-gray-500">{row.email}</p>
                </div>
              ),
            },
            {
              key: 'students',
              header: t('billing.families.column.students'),
              align: 'right',
              cell: (row) => row.studentsCount,
            },
            {
              key: 'balance',
              header: t('billing.families.column.balance'),
              align: 'right',
              sortable: true,
              sortValue: (row) => row.balance,
              cell: (row) => (
                <span className={row.balance > 0 ? 'font-medium text-gray-900' : 'text-gray-500'}>
                  {formatMoney(row.balance, row.currency)}
                </span>
              ),
            },
            {
              key: 'overdue',
              header: t('billing.families.column.overdue'),
              align: 'right',
              cell: (row) =>
                row.overdue > 0 ? (
                  <Badge tone="danger">{formatMoney(row.overdue, row.currency)}</Badge>
                ) : (
                  t('common.none')
                ),
            },
          ]}
        />
      )}
    </Card>
  );
};

const Concepts = () => {
  const t = useT();
  const api = useApi();
  const year = useActiveYear();
  const concepts = useQuery(billingQueries.concepts(api, year?.id ?? ''));
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<FeeConceptView | null>(null);
  const queries = useQueryClient();
  const refresh = () => queries.invalidateQueries({ queryKey: ['billing', 'concepts'] });
  if (!year)
    return (
      <Card title={t('billing.concepts.title')}>
        <p className="text-sm text-gray-500">{t('student.enroll.noYear')}</p>
      </Card>
    );
  return (
    <>
      <Card
        title={t('billing.concepts.title', { year: year.name })}
        flush
        actions={<Button onClick={() => setCreating(true)}>{t('billing.concepts.new')}</Button>}
      >
        {concepts.isError ? (
          <div className="p-6">
            <EmptyState
              title={t('entity.error.title')}
              description={describeError(t, concepts.error)}
            />
          </div>
        ) : (
          <Table<FeeConceptView>
            caption={t('billing.concepts.caption')}
            rows={concepts.data ?? []}
            rowKey={(row) => row.id}
            loading={concepts.isPending}
            onRowClick={(row) => setEditing(row)}
            empty={
              <EmptyState
                title={t('billing.concepts.empty.title')}
                description={t('billing.concepts.empty.body')}
                action={
                  <Button onClick={() => setCreating(true)}>{t('billing.concepts.new')}</Button>
                }
              />
            }
            columns={[
              {
                key: 'name',
                header: t('billing.concepts.column.name'),
                cell: (row) => (
                  <div>
                    <p className="font-medium text-gray-900">{row.name}</p>
                    <p className="text-xs text-gray-500">
                      {t(`billing.code.${row.code}`)}
                      {row.recurring ? ` · ${t('billing.concepts.recurring')}` : ''}
                    </p>
                  </div>
                ),
              },
              {
                key: 'tariffs',
                header: t('billing.concepts.column.tariffs'),
                cell: (row) =>
                  row.tariffs.length === 0 ? (
                    <Badge tone="warning">{t('billing.concepts.noTariffs')}</Badge>
                  ) : (
                    row.tariffs
                      .map(
                        (tariff) =>
                          `${tariff.gradeName ?? t('billing.tariffs.allGrades')}: ${formatMoney(tariff.amount)}`,
                      )
                      .join(' · ')
                  ),
              },
            ]}
          />
        )}
      </Card>
      <Modal open={creating} onClose={() => setCreating(false)} title={t('billing.concepts.new')}>
        {creating ? (
          <ConceptForm
            schoolYearId={year.id}
            onDone={() => {
              setCreating(false);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={t('billing.tariffs.title', { name: editing?.name ?? '' })}
        size="lg"
      >
        {editing ? (
          <TariffsForm
            concept={editing}
            onDone={() => {
              setEditing(null);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
    </>
  );
};

const ConceptForm = ({ schoolYearId, onDone }: { schoolYearId: string; onDone: () => void }) => {
  const t = useT();
  const api = useApi();
  const [code, setCode] = useState<FeeConceptCode>('matricula');
  const [name, setName] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      billingApi(api).createConcept({ schoolYearId, code, name: name.trim(), recurring }),
    onSuccess: onDone,
    onError: (thrown) => setError(describeError(t, thrown)),
  });
  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      {error ? (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      ) : null}
      <Select
        label={t('billing.concepts.field.code')}
        value={code}
        onChange={(event) => {
          const next = event.target.value as FeeConceptCode;
          setCode(next);
          setRecurring(next === 'pension' || next === 'transporte' || next === 'alimentacion');
        }}
        options={CODES.map((value) => ({ value, label: t(`billing.code.${value}`) }))}
      />
      <Input
        label={t('billing.concepts.field.name')}
        required
        value={name}
        onChange={(event) => setName(event.target.value)}
        help={t('billing.concepts.field.name.help')}
      />
      <Checkbox
        label={t('billing.concepts.recurring')}
        description={t('billing.concepts.recurring.help')}
        checked={recurring}
        onChange={(event) => setRecurring(event.target.checked)}
      />
      <div className="flex justify-end">
        <Button type="submit" loading={create.isPending}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
};

const TariffsForm = ({ concept, onDone }: { concept: FeeConceptView; onDone: () => void }) => {
  const t = useT();
  const api = useApi();
  const grades = useQuery(listQuery(api, 'grades'));
  const [general, setGeneral] = useState(
    String(concept.tariffs.find((row) => row.gradeId === null)?.amount ?? ''),
  );
  const [perGrade, setPerGrade] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      concept.tariffs
        .filter((row) => row.gradeId)
        .map((row) => [row.gradeId as string, String(row.amount)]),
    ),
  );
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      billingApi(api).putTariffs(concept.id, {
        tariffs: [
          ...(general.trim() ? [{ gradeId: null, amount: Number(general) }] : []),
          ...Object.entries(perGrade)
            .filter(([, amount]) => amount.trim() !== '')
            .map(([gradeId, amount]) => ({ gradeId, amount: Number(amount) })),
        ],
      }),
    onSuccess: onDone,
    onError: (thrown) => setError(describeError(t, thrown)),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <form className="space-y-3" onSubmit={submit}>
      {error ? (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      ) : null}
      <Input
        label={t('billing.tariffs.general')}
        help={t('billing.tariffs.general.help')}
        type="number"
        min={0}
        step={1}
        value={general}
        onChange={(event) => setGeneral(event.target.value)}
      />
      <div className="grid gap-2 sm:grid-cols-2">
        {(grades.data?.items ?? []).map((grade) => (
          <Input
            key={grade.id}
            label={grade.name}
            type="number"
            min={0}
            step={1}
            value={perGrade[grade.id] ?? ''}
            onChange={(event) =>
              setPerGrade((rows) => ({ ...rows, [grade.id]: event.target.value }))
            }
          />
        ))}
      </div>
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
};

const Generate = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const year = useActiveYear();
  const concepts = useQuery(billingQueries.concepts(api, year?.id ?? ''));
  const grades = useQuery(listQuery(api, 'grades'));
  const [conceptId, setConceptId] = useState('');
  const [gradeId, setGradeId] = useState('');
  const [dueOn, setDueOn] = useState(today());
  const [periodLabel, setPeriodLabel] = useState(year?.name ?? '');
  const [preview, setPreview] = useState<GenerateChargesResult | null>(null);
  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      billingApi(api).generate({
        feeConceptId: conceptId,
        ...(gradeId ? { gradeId } : {}),
        dueOn,
        periodLabel: periodLabel.trim(),
        dryRun,
      }),
    onSuccess: (result, dryRun) => {
      if (dryRun) setPreview(result);
      else {
        setPreview(null);
        void queries.invalidateQueries({ queryKey: ['billing'] });
        toast.show({
          tone: 'success',
          title: t('billing.generate.done'),
          description: t('billing.generate.summary', {
            created: result.created,
            skipped: result.skipped,
            total: formatMoney(result.totalAmount),
          }),
        });
      }
    },
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('billing.generate.title'),
        description: describeError(t, error),
      }),
  });
  const ready = conceptId !== '' && dueOn !== '' && periodLabel.trim() !== '';
  return (
    <Card title={t('billing.generate.title')}>
      <p className="mb-3 text-sm text-gray-600">{t('billing.generate.intro')}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label={t('billing.generate.concept')}
          value={conceptId}
          onChange={(event) => {
            setConceptId(event.target.value);
            setPreview(null);
          }}
          options={[
            { value: '', label: t('assignments.pick') },
            ...(concepts.data ?? []).map((concept) => ({ value: concept.id, label: concept.name })),
          ]}
        />
        <Select
          label={t('billing.generate.grade')}
          value={gradeId}
          onChange={(event) => {
            setGradeId(event.target.value);
            setPreview(null);
          }}
          options={[
            { value: '', label: t('billing.generate.allGrades') },
            ...(grades.data?.items ?? []).map((grade) => ({ value: grade.id, label: grade.name })),
          ]}
        />
        <Input
          label={t('billing.generate.dueOn')}
          type="date"
          required
          value={dueOn}
          onChange={(event) => {
            setDueOn(event.target.value);
            setPreview(null);
          }}
        />
        <Input
          label={t('billing.generate.period')}
          help={t('billing.generate.period.help')}
          required
          value={periodLabel}
          onChange={(event) => {
            setPeriodLabel(event.target.value);
            setPreview(null);
          }}
        />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          variant="secondary"
          disabled={!ready || run.isPending}
          onClick={() => run.mutate(true)}
        >
          {t('billing.generate.preview')}
        </Button>
        <Button
          disabled={!preview || preview.created === 0 || run.isPending}
          onClick={() => run.mutate(false)}
        >
          {t('billing.generate.confirm', { count: preview?.created ?? 0 })}
        </Button>
      </div>
      {preview ? (
        <div className="mt-4 space-y-3">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(
              [
                ['created', preview.created],
                ['skipped', preview.skipped],
                ['withoutFamily', preview.withoutFamily.length],
              ] as const
            ).map(([key, value]) => (
              <div key={key} className="rounded-control bg-gray-50 p-3">
                <dt className="text-xs text-gray-500">{t(`billing.generate.totals.${key}`)}</dt>
                <dd className="text-lg font-semibold text-gray-900">{value}</dd>
              </div>
            ))}
            <div className="rounded-control bg-gray-50 p-3">
              <dt className="text-xs text-gray-500">{t('billing.generate.totals.total')}</dt>
              <dd className="text-lg font-semibold text-gray-900">
                {formatMoney(preview.totalAmount)}
              </dd>
            </div>
          </dl>
          {preview.withoutFamily.length > 0 ? (
            <p role="status" className="text-sm text-warning-700">
              {t('billing.generate.withoutFamily', {
                names: preview.withoutFamily
                  .map((row) => row.studentName ?? row.studentUserId)
                  .join(', '),
              })}
            </p>
          ) : null}
          {(preview.preview ?? []).length > 0 ? (
            <Table
              caption={t('billing.generate.previewCaption')}
              rows={preview.preview ?? []}
              rowKey={(row) => row.studentUserId}
              columns={[
                {
                  key: 'student',
                  header: t('students.column.name'),
                  cell: (row) => row.studentName ?? row.studentUserId,
                },
                {
                  key: 'group',
                  header: t('students.column.group'),
                  cell: (row) => row.groupName ?? t('common.none'),
                },
                {
                  key: 'amount',
                  header: t('billing.statement.column.amount'),
                  align: 'right',
                  cell: (row) => formatMoney(row.amount),
                },
                {
                  key: 'discount',
                  header: t('billing.statement.column.discount'),
                  align: 'right',
                  cell: (row) =>
                    row.discountAmount > 0 ? formatMoney(row.discountAmount) : t('common.none'),
                },
              ]}
            />
          ) : null}
        </div>
      ) : null}
    </Card>
  );
};

const Export = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const [kind, setKind] = useState<'charges' | 'payments'>('charges');
  const [from, setFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [to, setTo] = useState(today());
  const download = useMutation({
    mutationFn: (format: 'csv' | 'xlsx') => billingApi(api).exportFile(kind, from, to, format),
    onSuccess: ({ blob, filename }) => saveBlob(blob, filename),
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('billing.export.title'),
        description: describeError(t, error),
      }),
  });
  return (
    <Card title={t('billing.export.title')}>
      <p className="mb-3 text-sm text-gray-600">{t('billing.export.intro')}</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Select
          label={t('billing.export.kind')}
          value={kind}
          onChange={(event) => setKind(event.target.value as 'charges' | 'payments')}
          options={[
            { value: 'charges', label: t('billing.export.charges') },
            { value: 'payments', label: t('billing.export.payments') },
          ]}
        />
        <Input
          label={t('billing.export.from')}
          type="date"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
        />
        <Input
          label={t('billing.export.to')}
          type="date"
          value={to}
          onChange={(event) => setTo(event.target.value)}
        />
      </div>
      <div className="mt-4 flex gap-2">
        <Button onClick={() => download.mutate('csv')} loading={download.isPending}>
          {t('billing.export.csv')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => download.mutate('xlsx')}
          disabled={download.isPending}
        >
          {t('billing.export.xlsx')}
        </Button>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        {formatDate(from)} → {formatDate(to)}
      </p>
    </Card>
  );
};
